"use client";

import { useState } from "react";
import { Badge } from "@workspace/ui/components/badge";
import { Input } from "@workspace/ui/components/input";
import { X } from "lucide-react";

interface TagBadgesProps {
  value?: string[];
  editing?: boolean;
  onChange?: (tags: string[]) => void;
  variant?: "secondary" | "outline";
}

export function TagBadges({
  value = [],
  editing,
  onChange,
  variant = "secondary",
}: TagBadgesProps) {
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const tag = inputValue.trim().toLowerCase();
      if (!value.includes(tag)) {
        onChange?.([...value, tag]);
      }
      setInputValue("");
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      onChange?.(value.slice(0, -1));
    }
  };

  if (!editing) {
    return value.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {value.map((tag) => (
          <Badge
            key={tag}
            variant={variant}
            className="text-sm px-2 py-0.5 font-normal"
          >
            {tag}
          </Badge>
        ))}
      </div>
    ) : (
      <span className="text-sm text-muted-foreground italic">—</span>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {value.map((tag) => (
          <Badge
            key={tag}
            variant={variant}
            className="text-sm px-2 py-0.5 font-normal gap-1"
          >
            {tag}
            <button
              type="button"
              onClick={() => onChange?.(value.filter((t) => t !== tag))}
              className="rounded-sm hover:bg-foreground/10"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Add tag + Enter"
        className="h-8 text-sm px-2"
      />
    </div>
  );
}
